import React, { useState, useEffect } from 'react';
import { Crown, Star, Check } from 'lucide-react';
import { getMyOrders, addReview, getUserReviews, BASE_URL, ENDPOINTS, getImageUrl } from '../../apiConfig';
import '../../pages/UserDashboard.css';

const GlobalOrderDeliveredPopup = () => {
  const [deliveredOrderForPopup, setDeliveredOrderForPopup] = useState(null);
  const [showDeliveredIntroPopup, setShowDeliveredIntroPopup] = useState(false);
  const [showProductReviewModal, setShowProductReviewModal] = useState(false);
  const [showReviewSuccessPopup, setShowReviewSuccessPopup] = useState(false);
  
  const [submittingReviewForId, setSubmittingReviewForId] = useState(null);
  const [reviewRatings, setReviewRatings] = useState({});
  const [reviewComments, setReviewComments] = useState({});
  const [reviewSuccessForProducts, setReviewSuccessForProducts] = useState({});
  
  const [allProducts, setAllProducts] = useState([]);
  const [userReviews, setUserReviews] = useState([]);

  useEffect(() => {
    fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}`)
      .then(res => res.json())
      .then(prodData => {
        const parsed = Array.isArray(prodData.data) ? prodData.data : (Array.isArray(prodData) ? prodData : []);
        setAllProducts(parsed);
      })
      .catch(err => console.error("Error fetching products:", err));

    const fetchReviews = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          const apiReviews = await getUserReviews();
          if (apiReviews) setUserReviews(apiReviews);
        }
      } catch (e) {}
    };
    fetchReviews();
  }, []);

  const getProductImage = (productIdOrObj, name = '') => {
    if (!productIdOrObj && !name) return 'https://via.placeholder.com/80x90?text=Fragrance';
    if (typeof productIdOrObj === 'string' && (productIdOrObj.startsWith('http') || productIdOrObj.startsWith('data:image'))) return productIdOrObj;
    if (typeof productIdOrObj === 'object' && productIdOrObj) {
      const img = productIdOrObj.images?.[0] || productIdOrObj.img || productIdOrObj.image;
      if (img) return getImageUrl(img);
    }
    const idStr = typeof productIdOrObj === 'object' && productIdOrObj ? (productIdOrObj._id || productIdOrObj.id) : String(productIdOrObj || '');
    if (idStr) {
      const found = allProducts.find(p => p._id === idStr || p.id === idStr);
      if (found) {
        const img = found.images?.[0] || found.img || found.image;
        if (img) return getImageUrl(img);
      }
    }
    return 'https://via.placeholder.com/80x90?text=Fragrance';
  };

  const getProductName = (productIdOrObj) => {
    if (!productIdOrObj) return 'Premium Fragrance';
    if (typeof productIdOrObj === 'object' && productIdOrObj.name) return productIdOrObj.name;
    const idStr = typeof productIdOrObj === 'object' ? (productIdOrObj._id || productIdOrObj.id) : String(productIdOrObj || '');
    const found = allProducts.find(p => p._id === idStr || p.id === idStr);
    if (found && found.name) return found.name;
    return idStr && idStr.length > 3 && !/^[0-9a-fA-F]{24}$/.test(idStr) ? idStr : 'Premium Fragrance';
  };

  const checkDeliveredOrders = (orders) => {
    if (!orders || orders.length === 0) return;
    const delivered = orders.find(o => {
      const status = o.status || o.orderStatus || '';
      return status.toLowerCase() === 'delivered';
    });
    
    if (delivered) {
      const orderId = delivered._id || delivered.id || '';
      if (orderId) {
        let acknowledged = [];
        try {
          acknowledged = JSON.parse(localStorage.getItem('acknowledged_delivered_orders') || '[]');
        } catch {}
        if (!acknowledged.includes(orderId)) {
          setDeliveredOrderForPopup(prev => prev || delivered);
          setShowDeliveredIntroPopup(prev => {
            // Only show intro if review modal and success modal are also closed
            if (!showProductReviewModal && !showReviewSuccessPopup) return true;
            return prev;
          });
        }
      }
    }
  };

  useEffect(() => {
    const pollOrders = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;
      try {
        const apiOrders = await getMyOrders();
        let localOrders = [];
        try {
          localOrders = JSON.parse(localStorage.getItem('my_placed_orders') || '[]');
        } catch {}
        const merged = [...(apiOrders || [])];
        localOrders.forEach(lo => {
          const apiIndex = merged.findIndex(ao => ao._id === lo._id || ao.id === lo.id);
          if (apiIndex === -1) {
            merged.unshift(lo);
          } else {
            // Override with local status if it was simulated as delivered
            if ((lo.status || lo.orderStatus || '').toLowerCase() === 'delivered') {
              merged[apiIndex] = lo;
            }
          }
        });
        checkDeliveredOrders(merged);
      } catch (err) {}
    };

    pollOrders();
    const intervalId = setInterval(pollOrders, 3000); // Check every 3 seconds globally
    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isModalOpen = deliveredOrderForPopup || showDeliveredIntroPopup || showProductReviewModal || showReviewSuccessPopup;
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }
  }, [deliveredOrderForPopup, showDeliveredIntroPopup, showProductReviewModal, showReviewSuccessPopup]);

  const handleSubmitProductReview = async (productId, rating, comment) => {
    if (!rating) {
      alert("Please select a star rating first.");
      return;
    }
    setSubmittingReviewForId(productId);
    try {
      let storedUser = {};
      try { storedUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}
      await addReview({
        productId,
        rating,
        comment: comment || "An absolutely exquisite luxury fragrance, highly recommended!",
        title: "Verified Purchase Review",
        name: storedUser.fullName || storedUser.name || "Escentrum Connoisseur",
        email: storedUser.email || "member@escentrum.com"
      });
      
      setReviewSuccessForProducts(prev => ({ ...prev, [productId]: true }));
      setShowReviewSuccessPopup(true);
    } catch (err) {
      alert(err.message || "Failed to submit review.");
    } finally {
      setSubmittingReviewForId(null);
    }
  };

  if (!showDeliveredIntroPopup && !showProductReviewModal && !showReviewSuccessPopup) return null;

  return (
    <>
      {showDeliveredIntroPopup && deliveredOrderForPopup && (
        <div className="ud-delete-modal-overlay" style={{ zIndex: 999999 }}>
          <div className="ud-delete-modal-content" style={{ maxWidth: '520px', width: '92%', borderColor: 'rgba(212, 175, 55, 0.2)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.2)', color: '#d4af37' }}>
              <Crown size={32} />
            </div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.9rem', color: '#111111', margin: '5px 0 10px' }}>Order Delivered!</h3>
            <p style={{ fontSize: '0.88rem', color: 'rgba(0, 0, 0, 0.60)', margin: '0 0 25px', lineHeight: '1.6', textAlign: 'center' }}>
              We are delighted to inform you that your Escentrum luxury order <span style={{ color: '#d4af37', fontWeight: '600' }}>#{deliveredOrderForPopup._id || deliveredOrderForPopup.id}</span> has successfully arrived. We hope you adore your luxury fragrances!
            </p>
            <div className="ud-delete-modal-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
              <button 
                className="ud-submit-review-btn" 
                style={{ width: '100%', padding: '14px', fontSize: '0.88rem', margin: 0 }}
                onClick={() => {
                  const orderId = deliveredOrderForPopup._id || deliveredOrderForPopup.id;
                  let acknowledged = [];
                  try { acknowledged = JSON.parse(localStorage.getItem('acknowledged_delivered_orders') || '[]'); } catch {}
                  if (!acknowledged.includes(orderId)) {
                    localStorage.setItem('acknowledged_delivered_orders', JSON.stringify([...acknowledged, orderId]));
                  }
                  setShowDeliveredIntroPopup(false);
                  setShowProductReviewModal(true);
                }}
              >
                Add Review
              </button>
              <button 
                className="ud-done-btn" 
                style={{ width: '100%', padding: '12px', fontSize: '0.82rem', margin: 0, borderColor: 'rgba(0, 0, 0, 0.10)' }}
                onClick={() => {
                  const orderId = deliveredOrderForPopup._id || deliveredOrderForPopup.id;
                  let acknowledged = [];
                  try { acknowledged = JSON.parse(localStorage.getItem('acknowledged_delivered_orders') || '[]'); } catch {}
                  if (!acknowledged.includes(orderId)) {
                    localStorage.setItem('acknowledged_delivered_orders', JSON.stringify([...acknowledged, orderId]));
                  }
                  setShowDeliveredIntroPopup(false);
                  setDeliveredOrderForPopup(null);
                }}
              >
                Skip & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showProductReviewModal && deliveredOrderForPopup && (
        <div className="ud-delete-modal-overlay" style={{ zIndex: 999999 }}>
          <div className="ud-delete-modal-content" style={{ maxWidth: '580px', width: '92%', borderColor: 'rgba(212, 175, 55, 0.15)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.2)', color: '#d4af37' }}>
              <Star size={28} fill="#d4af37" />
            </div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#111111', margin: '5px 0 10px' }}>Rate Purchased Items</h3>
            <p style={{ fontSize: '0.82rem', color: 'rgba(0, 0, 0, 0.50)', margin: '0 0 20px', lineHeight: '1.4' }}>
              Your feedback shapes the Escentrum experience. Select stars and type your thoughts below to review each item.
            </p>
            
            <div className="ud-delivered-products-list">
              {(() => {
                const itemsList = (deliveredOrderForPopup.items && deliveredOrderForPopup.items.length > 0)
                  ? deliveredOrderForPopup.items
                  : [{
                      product: deliveredOrderForPopup._id || deliveredOrderForPopup.id || 'mock-fragrance-id',
                      productId: deliveredOrderForPopup._id || deliveredOrderForPopup.id || 'mock-fragrance-id',
                      name: deliveredOrderForPopup.name || 'Escentrum Premium Oud Fragrance',
                      price: deliveredOrderForPopup.price || 'PKR 30,000'
                    }];

                return itemsList.map((item, idx) => {
                  const rawProductVal = item.product || item.productId;
                  let pId = '';
                  if (rawProductVal) {
                    pId = typeof rawProductVal === 'object' ? String(rawProductVal._id || rawProductVal.id || '') : String(rawProductVal);
                  }
                  if (!pId || pId === 'undefined') pId = 'prod-' + (item.name || 'unnamed').replace(/\s+/g, '-').toLowerCase();

                  const pName = item.name || getProductName(pId);
                  const pImg = getProductImage(item.product || item.productId || pId);
                  const rawReviewed = userReviews.some(r => String(r.productId || (r.product && (r.product._id || r.product.id)) || '') === pId) || reviewSuccessForProducts[pId];
                  const currentRating = reviewRatings[pId] || 0;
                  const currentComment = reviewComments[pId] || '';
                  
                  return (
                    <div key={idx} className="ud-delivered-product-row">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
                          <img 
                            src={pImg} 
                            alt={pName} 
                            style={{ width: '48px', height: '54px', borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(0, 0, 0, 0.04)' }}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/80x90?text=Fragrance'; }}
                          />
                          <div style={{ flex: 1 }}>
                            <h4 style={{ margin: 0, fontSize: '0.94rem', color: '#111111', fontWeight: '600', fontFamily: "'Inter', sans-serif" }}>{pName}</h4>
                            <span style={{ fontSize: '0.7rem', color: 'rgba(0, 0, 0, 0.40)', fontWeight: '500' }}>
                              Verified Luxury Purchase
                            </span>
                          </div>
                        </div>
                        {rawReviewed && (
                          <div className="ud-reviewed-badge" style={{ margin: 0 }}>
                            <Check size={12} /> Reviewed
                          </div>
                        )}
                      </div>
                      
                      {!rawReviewed && (
                        <div className="ud-review-form-container" style={{ display: 'block', marginTop: '12px' }}>
                          <p style={{ margin: '0 0 8px', fontSize: '0.72rem', color: 'rgba(0, 0, 0, 0.40)', fontWeight: '600', letterSpacing: '0.5px' }}>YOUR RATING</p>
                          <div className="ud-star-rating-selector">
                            {[1, 2, 3, 4, 5].map((starVal) => (
                              <button 
                                key={starVal}
                                type="button"
                                className={`ud-star-btn ${currentRating >= starVal ? 'active' : ''}`}
                                onClick={() => setReviewRatings(prev => ({ ...prev, [pId]: starVal }))}
                              >
                                <Star size={20} fill={currentRating >= starVal ? '#d4af37' : 'transparent'} />
                              </button>
                            ))}
                          </div>
                          
                          <textarea 
                            className="ud-textarea-luxury"
                            placeholder="Share your thoughts on the sillage, aroma, and longevity of this exquisite fragrance..."
                            value={currentComment}
                            onChange={(e) => setReviewComments(prev => ({ ...prev, [pId]: e.target.value }))}
                          />
                          
                          <button 
                            className="ud-submit-review-btn"
                            style={{ width: '100%', margin: '12px 0 0' }}
                            disabled={submittingReviewForId === pId}
                            onClick={() => handleSubmitProductReview(pId, currentRating, currentComment)}
                          >
                            {submittingReviewForId === pId ? 'Posting...' : 'Post Review'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            
            <button 
              className="ud-done-btn"
              onClick={() => {
                const orderId = deliveredOrderForPopup._id || deliveredOrderForPopup.id;
                let acknowledged = [];
                try { acknowledged = JSON.parse(localStorage.getItem('acknowledged_delivered_orders') || '[]'); } catch {}
                if (!acknowledged.includes(orderId)) {
                  localStorage.setItem('acknowledged_delivered_orders', JSON.stringify([...acknowledged, orderId]));
                }
                setShowProductReviewModal(false);
                setDeliveredOrderForPopup(null);
              }}
            >
              Done & Close
            </button>
          </div>
        </div>
      )}

      {showReviewSuccessPopup && (
        <div className="ud-delete-modal-overlay" style={{ zIndex: 999999 }}>
          <div className="ud-delete-modal-content ud-success-content" style={{ maxWidth: '420px', width: '92%', borderColor: 'rgba(34, 197, 94, 0.2)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '2px solid rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
              <Check size={32} />
            </div>
            <h3 style={{ color: '#111111', fontSize: '1.4rem', margin: '5px 0 10px' }}>Review Submitted!</h3>
            <p style={{ fontSize: '0.85rem', color: 'rgba(0, 0, 0, 0.60)', margin: '0 0 20px', lineHeight: '1.5', textAlign: 'center' }}>
              Thank you for sharing your luxury fragrance experience. Your review has been added to our exquisite fragrance library.
            </p>
            <button 
              className="ud-submit-review-btn"
              style={{ width: '100%', margin: 0, padding: '12px' }}
              onClick={() => setShowReviewSuccessPopup(false)}
            >
              Excellent
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalOrderDeliveredPopup;
